/**
 * Notifications Container Component
 *
 * Displays success and error notifications for settings operations using core NotificationMessage component.
 *
 * @since 1.0.0
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { NotificationMessage } from "../notification-message";
import { NotificationAnimationWrapper, NotificationsWrapper } from "./styled";
import type { NotificationsContainerProps, NotificationState } from "./types";

/**
 * Notifications Container Component
 *
 * @since 1.0.0
 *
 * @param props Component props.
 *
 * @return JSX element.
 */
export const NotificationsContainer = ({
  notifications,
  onDismiss,
}: NotificationsContainerProps): JSX.Element | null => {
  const [notificationStates, setNotificationStates] = useState<
    NotificationState[]
  >([]);
  const activeTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(
    new Map(),
  );

  // Update notification states when notifications prop changes
  useEffect(() => {
    setNotificationStates((prevStates) => {
      const newStates: NotificationState[] = [];

      // Handle existing notifications
      notifications.forEach((notification) => {
        const existingState = prevStates.find(
          (state) => state.id === notification.id,
        );
        if (existingState) {
          // Keep existing state but update data
          newStates.push({
            ...notification,
            isVisible: existingState.isVisible,
            isExiting: existingState.isExiting,
          });
        } else {
          // New notification - start hidden, will animate in
          newStates.push({
            ...notification,
            isVisible: false,
            isExiting: false,
          });
        }
      });

      // Keep exiting notifications until animation completes
      prevStates.forEach((prevState) => {
        if (
          prevState.isExiting &&
          !notifications.find((n) => n.id === prevState.id)
        ) {
          newStates.push(prevState);
        }
      });

      return newStates;
    });
  }, [notifications]);

  // Animate new notifications in
  useEffect(() => {
    const newNotifications = notificationStates.filter(
      (state) => !state.isVisible && !state.isExiting,
    );

    if (newNotifications.length > 0) {
      // Small delay to ensure DOM is updated before animation
      const timer = setTimeout(() => {
        setNotificationStates((prevStates) =>
          prevStates.map((state) =>
            newNotifications.find((n) => n.id === state.id)
              ? { ...state, isVisible: true }
              : state,
          ),
        );
      }, 50);

      return () => clearTimeout(timer);
    }
    return () => {};
  }, [notificationStates]);

  // Handle dismiss with animation
  const handleDismiss = useCallback(
    (id: string) => {
      // Clear any active timer for this notification
      const timer = activeTimersRef.current.get(id);
      if (timer) {
        clearTimeout(timer);
        activeTimersRef.current.delete(id);
      }

      setNotificationStates((prevStates) =>
        prevStates.map((state) =>
          state.id === id ? { ...state, isExiting: true } : state,
        ),
      );

      // Remove after animation completes
      setTimeout(() => {
        setNotificationStates((prevStates) =>
          prevStates.filter((state) => state.id !== id),
        );
        onDismiss(id);
      }, 300);
    },
    [onDismiss],
  );

  // Auto-dismiss notifications
  useEffect(() => {
    notificationStates.forEach((notification) => {
      // Only set timer if notification is visible, not exiting, and doesn't already have a timer
      if (
        notification.isVisible &&
        !notification.isExiting &&
        !activeTimersRef.current.has(notification.id)
      ) {
        const delay = notification.variant === "success" ? 5000 : 8000;

        const timer = setTimeout(() => {
          handleDismiss(notification.id);
          activeTimersRef.current.delete(notification.id);
        }, delay);

        activeTimersRef.current.set(notification.id, timer);
      }
    });

    // Clean up timers for notifications that no longer exist
    const currentIds = new Set(notificationStates.map((n) => n.id));
    activeTimersRef.current.forEach((timer, id) => {
      if (!currentIds.has(id)) {
        clearTimeout(timer);
        activeTimersRef.current.delete(id);
      }
    });
  }, [notificationStates, handleDismiss]);

  // Clean up all timers on unmount
  useEffect(() => {
    const timersMap = activeTimersRef.current;
    return () => {
      timersMap.forEach((timer) => clearTimeout(timer));
      timersMap.clear();
    };
  }, []);

  if (notificationStates.length === 0) {
    return null;
  }

  return (
    <NotificationsWrapper>
      {notificationStates.map((notification) => (
        <NotificationAnimationWrapper
          key={notification.id}
          $isVisible={notification.isVisible}
          $isExiting={notification.isExiting}
        >
          <NotificationMessage
            variant={notification.variant}
            dismissible={true}
            onDismiss={() => handleDismiss(notification.id)}
            visible={true} // Always visible - animation handled by wrapper
            autoHide={false} // We handle auto-hide manually
            autoHideDelay={0}
          >
            {notification.message}
          </NotificationMessage>
        </NotificationAnimationWrapper>
      ))}
    </NotificationsWrapper>
  );
};
